import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import { FeaturesReady, useFeatureValue } from '@growthbook/growthbook-react';

import Icon from 'dpl/common/components/Icon';
import LoadingDots from 'dpl/components/LoadingDots';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_USER_JOURNEYS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { GB_FEATURES } from 'dpl/shared/constants/gb_features';
import { pushParams } from 'dpl/actions/query_params';
import { FOLLOWABLE_ID, FOLLOWABLE_TYPE } from 'dpl/constants/query_params';
import { SIGNUP_FORM_TYPES } from 'dpl/shared/constants/shared';
import {
  SIGNUP_REF_ACTION,
  SIGNUP_REF_ACTION_TYPES
} from 'dpl/auth/utils/constants';
import { isMobileUA } from 'dpl/shared/utils';

import useUserFollowsDataV2 from '../hooks/useUserFollowsDataV2';
import useFollowersData from '../hooks/useFollowersData';
import FavoriteEngagementMobileMenu from './FavoriteEngagementMobileMenu';
import useBuyerFavoritesSummaries from '../hooks/useBuyerFavoritesSummaries';
import { FOLLOWABLE_TYPES } from '../utils/constants';

const IS_MOBILE_UA = isMobileUA();
const DEFAULT_ICON_SIZE = '24px';

type IconConfig = {
  name: string;
  className: string;
  height: string;
  width: string;
};

const ICON_PROPS: { CHECKED: IconConfig; UNCHECKED: IconConfig } = {
  CHECKED: {
    name: 'fetch-heart-full-breeder',
    className: 'red o-100',
    height: DEFAULT_ICON_SIZE,
    width: DEFAULT_ICON_SIZE
  },
  UNCHECKED: {
    name: 'fetch-heart',
    className: 'default-color',
    height: DEFAULT_ICON_SIZE,
    width: DEFAULT_ICON_SIZE
  }
};

function getButtonProps({
  checked,
  isAlt
}: {
  checked: boolean;
  isAlt: boolean;
}): { label: string; iconProps: IconConfig } {
  if (isAlt && !checked) {
    return {
      label: 'Get updates',
      iconProps: {
        ...ICON_PROPS.UNCHECKED,
        name: 'fetch-bell'
      }
    };
  }

  if (checked) {
    return {
      label: 'Following',
      iconProps: ICON_PROPS.CHECKED
    };
  }

  return {
    label: 'Follow',
    iconProps: ICON_PROPS.UNCHECKED
  };
}

interface AnalyticsProps {
  breeder_name?: string;
  view?: string;
  view_feature?: string;
  view_feature_location?: string;
  // Allow additional analytics fields to be passed through
  [key: string]: unknown;
}

export interface BreederProfileFollowButtonProps {
  analytics?: AnalyticsProps;
  breederFirstName?: string;
  breederProfilePhotoUrl?: string;
  className?: string;
  breederBreedIds: number[];
  breederProfileId: number | string;
  withIcon?: boolean;
}

function BreederProfileFollowButtonContent({
  analytics = {},
  breederFirstName,
  breederProfilePhotoUrl,
  className,
  breederBreedIds,
  breederProfileId,
  withIcon = false
}: BreederProfileFollowButtonProps) {
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const currentUser = useCurrentUser();
  const { isLoggedIn, value: currentUserData } = currentUser;
  const { followersData } = useFollowersData(breederProfileId);
  const { summariesData, refetchAndCheckHasTwoFavorites } =
    useBuyerFavoritesSummaries(currentUserData?.data?.buyer_profile_id);
  const featureValue = useFeatureValue<string | null>(
    GB_FEATURES.EXP_ALT_FOLLOW_BREEDER_CTA,
    null
  );
  const dispatch = useDispatch();

  const {
    createBreederProfileUserFollow,
    removeBreederProfileFollow,
    userFollowsData,
    isFollowingBreeder,
    isLoading,
    isUpdating
  } = useUserFollowsDataV2(breederBreedIds, breederProfileId);

  const { label, iconProps } = getButtonProps({
    isAlt: featureValue === 'variant',
    checked: isFollowingBreeder
  });

  async function updateFavorite(buyerProfileID?: number | string) {
    if (!isFollowingBreeder) {
      const eventName = ANALYTICS_EVENTS.BREEDER_ADDED_TO_FAVORITES;

      sendRudderstackEvent(eventName, {
        buyer_profile_id:
          currentUserData?.data?.buyer_profile_id || buyerProfileID,
        breeder_profile_id: breederProfileId,
        user_journey: ANALYTICS_USER_JOURNEYS.BUYER_BROWSING,
        ...analytics
      });

      try {
        await createBreederProfileUserFollow({
          body: {
            breeder_profile_id: breederProfileId
          }
        });
      } finally {
        await userFollowsData.refetch();
        await followersData.refetch();
        const isSecondFavorite = await refetchAndCheckHasTwoFavorites();

        if (IS_MOBILE_UA && isSecondFavorite) {
          setTimeout(() => setShowEngagementModal(true), 1500);
        }
      }
    } else {
      const eventName = ANALYTICS_EVENTS.BREEDER_REMOVED_FROM_FAVORITES;

      sendRudderstackEvent(eventName, {
        buyer_profile_id: currentUserData?.data?.buyer_profile_id,
        breeder_profile_id: breederProfileId,
        user_journey: ANALYTICS_USER_JOURNEYS.BUYER_BROWSING,
        ...analytics
      });

      try {
        await removeBreederProfileFollow({});
      } finally {
        await userFollowsData.refetch();
        await followersData.refetch();
        await summariesData.refetch();
      }
    }
  }

  if (withIcon && isLoading) {
    return (
      <div className={className}>
        <LoadingDots height="20px" isVisible />
      </div>
    );
  }

  function handleFavoriteClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    e.preventDefault();

    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: (e.target as HTMLElement).textContent,
      view: analytics?.view,
      view_feature: ANALYTICS_VIEW_FEATURES.FOLLOW_BUTTON,
      view_feature_location: analytics?.view_feature_location,
      buyer_profile_id: currentUserData?.data?.buyer_profile_id,
      breeder_profile_id: breederProfileId
    });

    if (!isLoggedIn) {
      dispatch(
        pushParams(
          {
            direct_signup: SIGNUP_FORM_TYPES.SIGNUP,
            [SIGNUP_REF_ACTION]: SIGNUP_REF_ACTION_TYPES.FAVORITE_BUTTON,
            [FOLLOWABLE_TYPE]: FOLLOWABLE_TYPES.BREEDER_PROFILE,
            [FOLLOWABLE_ID]: breederProfileId
          },
          { hash: true }
        )
      );
    } else {
      updateFavorite();
    }
  }

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={handleFavoriteClick}
        disabled={isLoading || isUpdating}
      >
        {withIcon && <Icon {...iconProps} />}
        <p
          className={classnames({
            ml2: withIcon
          })}
        >
          {label}
        </p>
      </button>
      <FavoriteEngagementMobileMenu
        analytics={{ view: ANALYTICS_VIEWS.BREEDER_PROFILE }}
        isOpen={showEngagementModal}
        onCloseClick={() => setShowEngagementModal(false)}
        variant="breeder"
        breederFirstName={breederFirstName}
        breederProfilePhotoUrl={breederProfilePhotoUrl}
      />
    </>
  );
}

export default function BreederProfileFollowButton(
  props: BreederProfileFollowButtonProps
) {
  return (
    <FeaturesReady timeout={500}>
      <BreederProfileFollowButtonContent {...props} />
    </FeaturesReady>
  );
}
